import React, { useState } from 'react';
import { healScheduleLinks } from '../../../api/adminApi';

/** What each healer repairs, in the order the endpoint runs them. */
const HEALER_LABELS = {
  crossCourseExam: 'Exam linked in the wrong course',
  level2Exam: 'Level-2 exam variant',
  termExamSets: 'Term exam sets',
  ghostExam: 'Ghost exam (nobody attempted it)',
  missingExam: 'Exam session with no exam linked',
  videoConference: 'Live class on the wrong video conference',
  liveVcByAttendance: 'Live class the batch did not join',
};

const SCOPES = [
  { value: 'all', label: 'Exams and live conferences' },
  { value: 'exam', label: 'Exams only' },
  { value: 'vc', label: 'Live conferences only' },
];

/** A healer's result object carries different counters; pull out the ones that moved. */
const changeCount = (result) => {
  if (!result) return 0;
  return (result.healed ?? 0)
    + (result.healedByAttempts ?? 0)
    + (result.healedByWindow ?? 0)
    + (result.repointed ?? 0)
    + (result.created ?? 0)
    + (result.linked ?? 0)
    + (result.added ?? 0);
};

const scannedCount = (result) => result?.scanned ?? 0;

/**
 * Runs the schedule link healers over a date range and shows what they would
 * change before anything is written.
 *
 * Preview is the default and the only way to reach Apply, so a repair is always
 * seen before it lands. See docs/api/SCHEDULE_LINK_HEALING.md.
 */
const HealPanel = ({ batchId = null, isoDate, month, onHealed }) => {
  const [scope, setScope] = useState('all');
  const [wholeMonth, setWholeMonth] = useState(false);
  const [allBatches, setAllBatches] = useState(!batchId);
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // The month's last day: day 0 of the next month.
  const monthRange = () => {
    const [year, monthNumber] = month.split('-').map(Number);
    const lastDay = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
    return { from: `${month}-01`, to: `${month}-${String(lastDay).padStart(2, '0')}` };
  };

  const range = wholeMonth ? monthRange() : { from: isoDate, to: isoDate };

  const run = async (dryRun) => {
    setBusy(true);
    setError(null);
    try {
      const payload = { ...range, scope, dryRun };
      if (!allBatches && batchId) payload.batchId = batchId;

      const result = await healScheduleLinks(payload);
      setPreview(result);
      if (!dryRun) {
        setApplied(result);
        if (onHealed) onHealed(result);
      }
    } catch (healError) {
      setError(healError?.response?.data?.message || 'Healing failed.');
    } finally {
      setBusy(false);
    }
  };

  const totalChanges = preview
    ? Object.values(preview.results || {}).reduce((sum, result) => sum + changeCount(result), 0)
    : 0;

  return (
    <div className="cal-heal">
      <div className="cal-heal-head">
        <i className="bi bi-bandaid" aria-hidden="true" />
        <strong>Repair stale links</strong>
        <span className="sb-selcount">
          Re-points schedules at the exam or conference the batch actually used.
        </span>
      </div>

      <div className="cal-heal-controls">
        <select
          className="cal-select"
          value={scope}
          onChange={(event) => { setScope(event.target.value); setPreview(null); setApplied(null); }}
          aria-label="What to repair"
        >
          {SCOPES.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>

        <label className="cal-heal-check">
          <input
            type="checkbox"
            checked={wholeMonth}
            onChange={(event) => { setWholeMonth(event.target.checked); setPreview(null); setApplied(null); }}
          />
          Whole month
        </label>

        <label className="cal-heal-check">
          <input
            type="checkbox"
            checked={allBatches}
            onChange={(event) => { setAllBatches(event.target.checked); setPreview(null); setApplied(null); }}
          />
          All batches
        </label>

        <button type="button" className="sb-btn sb-btn-sm" disabled={busy} onClick={() => run(true)}>
          {busy && !applied ? 'Checking…' : 'Preview'}
        </button>
        <button
          type="button"
          className="sb-btn sb-btn-sm sb-btn-primary"
          disabled={busy || !preview || totalChanges === 0 || applied !== null}
          onClick={() => run(false)}
        >
          {applied ? 'Applied' : `Apply ${totalChanges || ''}`.trim()}
        </button>
      </div>

      {error && <div className="cal-error">{error}</div>}

      {preview && (
        <div className="cal-heal-results">
          <div className="cal-heal-summary">
            {totalChanges === 0
              ? 'Nothing to repair in this range — every link already points at the right content.'
              : `${totalChanges} link${totalChanges === 1 ? '' : 's'} would be re-pointed.`}
            {preview.batchId ? ' This batch only.' : ' All batches.'}
          </div>

          <table className="cal-heal-table">
            <thead>
              <tr><th>Repair</th><th>Scanned</th><th>Changes</th></tr>
            </thead>
            <tbody>
              {Object.entries(preview.results || {}).map(([name, result]) => (
                <tr key={name} className={changeCount(result) > 0 ? 'has-change' : ''}>
                  <td>{HEALER_LABELS[name] || name}</td>
                  <td className="num">{scannedCount(result)}</td>
                  <td className="num">{changeCount(result)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {preview.failures && (
            <div className="cal-error">
              Some repairs could not run:{' '}
              {Object.entries(preview.failures).map(([name, message]) => (
                <div key={name}>{HEALER_LABELS[name] || name}: {message}</div>
              ))}
            </div>
          )}

          {applied && (
            <div className="sb-result">Applied — the calendar has been reloaded.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default HealPanel;
